/**
 * ADM-05 课程 SKU 列表页（subject-course-sku:SC-18~SC-45 前端）。
 *
 * 筛选区：名称/编码 + 科目下拉 + 学段 + 状态（含已停用）+ 查询/重置。
 * 列表表格：SKU编码（点击打开只读详情抽屉）、名称、科目、学段、教材版本、
 *           标价（分转元）、课时数、试课次数、有效班级引用数（占位0）、状态标签、更新时间、操作。
 *           （O-14 起收费模式列/筛选退役：SKU 回归纯教学标准，售卖形态归商品配置行）
 * 行操作：编辑（草稿/已下架可编辑，已上架禁用并提示）、停用（二次确认，处理在售商品引用阻断）。
 * 读取 URL ?subjectId= 预填筛选（科目页「查看关联SKU」跳转）。
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Card, Input, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { message } from '../../utils/antd-message';
import { PlusOutlined, ReloadOutlined } from "@ant-design/icons";
import { EllipsisLink, EllipsisText } from "../../components/EllipsisCell";
import { useResizableColumns } from "../../hooks/useResizableColumns";
import type { ColumnsType } from "antd/es/table";
import { useSearchParams } from "react-router-dom";
import {
  courseSkuApi,
  type CourseSkuItem,
  type CourseSkuListQuery,
} from "../../api/course-skus.api";
import { subjectsApi, type SubjectItem } from "../../api/subjects.api";
import {
  gradeStageLabel,
  shelfStatusLabel,
  toSelectOptions,
} from "../../api/labels";
import { ApiError } from "../../api/transport";
import { MoneyDisplay } from "../../components/MoneyDisplay";
import { useDraggableModal } from "../../components/useDraggableModal";
import {
  SkuEditModal,
  toCreateDto,
  toUpdateDto,
  type SkuFormValues,
} from "./SkuEditModal";
import { SkuDetailDrawer } from "./SkuDetailDrawer";
import { HasPermission, useHasPermission } from "../../auth/HasPermission";

const { Title } = Typography;

/** SKU 在售商品引用阻断错误码。 */
const SKU_REFERENCED_ERROR =
  "product-catalog.sku_referenced_by_on_shelf_products";

/** 状态标签颜色映射。 */
const shelfStatusColor: Record<CourseSkuItem["status"], string> = {
  DRAFT: "default",
  ON_SHELF: "green",
  OFF_SHELF: "orange",
  DISABLED: "red",
};

/** 提取错误信息文案。 */
function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "操作失败";
}

export function CourseSkusPage() {
  const modal = useDraggableModal();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState<CourseSkuItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState<CourseSkuListQuery>({});
  const [subjects, setSubjects] = useState<SubjectItem[]>([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<CourseSkuItem | null>(null);
  const [viewing, setViewing] = useState<CourseSkuItem | null>(null);

  // 科目下拉与名称映射用：加载全部科目（一期数据量小）
  useEffect(() => {
    subjectsApi
      .listSubjects({ pageSize: 100 })
      .then((res) => setSubjects(res.items))
      .catch(() => undefined);
  }, []);

  const subjectMap = useMemo(() => {
    const map = new Map<string, string>();
    subjects.forEach((s) => map.set(s.id, s.name));
    return map;
  }, [subjects]);

  const subjectOptions = useMemo(
    () => subjects.map((s) => ({ value: s.id, label: s.name })),
    [subjects],
  );

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const result = await courseSkuApi.listCourseSkus({
        ...filters,
        page,
        pageSize,
      });
      setData(result.items);
      setTotal(result.total);
    } catch (err) {
      message.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  // 初始化：读取 URL ?subjectId= 预填筛选（科目页跳转）
  useEffect(() => {
    const subjectId = searchParams.get("subjectId");
    if (subjectId) {
      setFilters((f) => ({ ...f, subjectId }));
      setSearchParams({}, { replace: true });
    }
  }, []);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const openCreate = () => {
    setEditing(null);
    setModalOpen(true);
  };

  const openEdit = (record: CourseSkuItem) => {
    setEditing(record);
    setModalOpen(true);
  };

  /** 暂存（新建/编辑保存）。 */
  const handlePersist = async (
    values: SkuFormValues,
    current: CourseSkuItem | null,
  ): Promise<CourseSkuItem> => {
    try {
      if (current) {
        const saved = await courseSkuApi.updateCourseSku(
          current.id,
          toUpdateDto(values, current.version),
        );
        message.success("已暂存");
        setModalOpen(false);
        void fetchList();
        return saved;
      }
      const created = await courseSkuApi.createCourseSku(toCreateDto(values));
      message.success("已暂存草稿");
      setModalOpen(false);
      void fetchList();
      return created;
    } catch (err) {
      message.error(errorMessage(err));
      throw err;
    }
  };

  /** 上架（编辑对话框调用，先保存后 publish）。 */
  const handlePublish = async (id: string): Promise<void> => {
    try {
      await courseSkuApi.publishCourseSku(id);
      message.success("已上架");
      setModalOpen(false);
      void fetchList();
    } catch (err) {
      message.error(errorMessage(err));
      throw err;
    }
  };

  /** 行内下架（已上架 SKU）。 */
  const handleUnpublish = async (id: string) => {
    try {
      await courseSkuApi.unpublishCourseSku(id);
      message.success("已下架");
      void fetchList();
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  /** 停用：二次确认，处理在售商品引用阻断。 */
  const handleDisable = async (id: string) => {
    try {
      await courseSkuApi.disableCourseSku(id);
      message.success("SKU 已停用");
      void fetchList();
    } catch (err) {
      if (err instanceof ApiError && err.code === SKU_REFERENCED_ERROR) {
        modal.warning({
          title: "无法停用",
          content: "该 SKU 正被在售商品引用，请先下架相关商品后再停用。",
        });
        return;
      }
      message.error(errorMessage(err));
    }
  };

  // 行操作统一 EDIT 闸控（对齐后端 @Roles）；无权限整列不渲染，越权由后端 403 兜底
  const canManage = useHasPermission("ADM-06", "EDIT");
  const { columns, components } = useResizableColumns<CourseSkuItem>([
    {
      title: "SKU编码",
      dataIndex: "code",
      width: 140,
      ellipsis: true,
      render: (code: string, record) => (
        <EllipsisLink value={code} onClick={() => setViewing(record)} />
      ),
    },
    {
      title: "名称",
      dataIndex: "name",
      width: 160,
      ellipsis: true,
      render: (v: string) => <EllipsisText value={v} />,
    },
    {
      title: "科目",
      dataIndex: "subjectId",
      width: 120,
      ellipsis: true,
      render: (subjectId: string) => {
        const name = subjectMap.get(subjectId);
        return name ?? <EllipsisText value={subjectId} />;
      },
    },
    {
      title: "年级/学段",
      dataIndex: "gradeStage",
      width: 120,
      render: (v: CourseSkuItem["gradeStage"]) => gradeStageLabel[v],
    },
    { title: "教材版本", dataIndex: "textbook", width: 120 },
    {
      title: "标价",
      dataIndex: "listPrice",
      width: 100,
      render: (v: number) => <MoneyDisplay amountInCents={v} />,
    },
    {
      title: "课时数",
      dataIndex: "entitlementQuantity",
      width: 80,
      align: "center",
    },
    {
      title: "试课次数",
      dataIndex: "trialSessionCount",
      width: 90,
      align: "center",
    },
    {
      title: "有效班级数",
      dataIndex: "activeClassRefCount",
      width: 120,
      align: "center",
    },
    {
      title: "状态",
      dataIndex: "status",
      width: 90,
      render: (status: CourseSkuItem["status"]) => (
        <Tag color={shelfStatusColor[status]}>{shelfStatusLabel[status]}</Tag>
      ),
    },
    {
      title: "更新时间",
      dataIndex: "updatedAt",
      width: 180,
      render: (v: string) => (v ? new Date(v).toLocaleString("zh-CN") : "-"),
    },
    ...(canManage
      ? ([
          {
            title: "操作",
            key: "action",
            width: 200,
            fixed: "right",
            render: (_, record) => {
              const canEdit =
                record.status === "DRAFT" || record.status === "OFF_SHELF";
              return (
                <Space size="small">
                  {canEdit ? (
                    <Button
                      type="link"
                      size="small"
                      onClick={() => openEdit(record)}
                    >
                      编辑
                    </Button>
                  ) : (
                    <Tooltip title="已上架 SKU 不可编辑，请先下架">
                      <Button type="link" size="small" disabled>
                        编辑
                      </Button>
                    </Tooltip>
                  )}
                  {record.status === "ON_SHELF" && (
                    <Popconfirm
                      title="确定下架该 SKU 吗？"
                      onConfirm={() => handleUnpublish(record.id)}
                      okText="确定"
                      cancelText="取消"
                    >
                      <Button type="link" size="small">
                        下架
                      </Button>
                    </Popconfirm>
                  )}
                  {record.status !== "DISABLED" && (
                    <Popconfirm
                      title="确定停用该 SKU 吗？"
                      description="停用后不可用于新商品打包与新班级引用"
                      onConfirm={() => handleDisable(record.id)}
                      okText="确定"
                      cancelText="取消"
                    >
                      <Button type="link" size="small" danger>
                        停用
                      </Button>
                    </Popconfirm>
                  )}
                </Space>
              );
            },
          },
        ] as ColumnsType<CourseSkuItem>)
      : []),
  ]);

  return (
    <div>
      <Title level={4}>课程SKU</Title>
      <Card style={{ marginBottom: 16 }}>
        <Space wrap>
          <Input
            placeholder="名称/编码"
            allowClear
            value={filters.name ?? filters.code ?? ""}
            onChange={(e) => {
              const keyword = e.target.value || undefined;
              setFilters((f) => ({ ...f, name: keyword, code: undefined }));
            }}
            style={{ width: 200 }}
          />
          <Select
            placeholder="科目"
            allowClear
            value={filters.subjectId}
            onChange={(val) =>
              setFilters((f) => ({ ...f, subjectId: val ?? undefined }))
            }
            style={{ width: 140 }}
            options={subjectOptions}
          />
          <Select
            placeholder="学段"
            allowClear
            value={filters.gradeStage}
            onChange={(val) =>
              setFilters((f) => ({ ...f, gradeStage: val ?? undefined }))
            }
            style={{ width: 140 }}
            options={toSelectOptions(gradeStageLabel)}
          />
          <Select
            placeholder="状态"
            allowClear
            value={filters.status}
            onChange={(val) =>
              setFilters((f) => ({ ...f, status: val ?? undefined }))
            }
            style={{ width: 120 }}
            options={toSelectOptions(shelfStatusLabel)}
          />
          <Button
            type="primary"
            icon={<ReloadOutlined />}
            onClick={() => void fetchList()}
          >
            查询
          </Button>
          <Button
            onClick={() => {
              setFilters({});
              setPage(1);
            }}
          >
            重置
          </Button>
        </Space>
      </Card>
      <Card>
        <div style={{ marginBottom: 16 }}>
          {/* 无 ADM-06 CREATE 权限不渲染入口，越权直调由后端 403 兜底 */}
          <HasPermission menuKey="ADM-06" action="CREATE">
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              新增
            </Button>
          </HasPermission>
        </div>
        <Table<CourseSkuItem>
          scroll={{ x: "max-content" }}
          rowKey="id"
          columns={columns}
          components={components}
          dataSource={data}
          loading={loading}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>
      <SkuEditModal
        open={modalOpen}
        editing={editing}
        subjectOptions={subjectOptions}
        onPersist={handlePersist}
        onPublish={handlePublish}
        onClose={() => setModalOpen(false)}
      />
      <SkuDetailDrawer
        sku={viewing}
        subjectName={viewing ? subjectMap.get(viewing.subjectId) : undefined}
        onClose={() => setViewing(null)}
      />
    </div>
  );
}
